import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ServerIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { deviceService, Device } from '../services/api.ts';

function Reports() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Device state
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  // Fetch devices
  const fetchDevices = async () => {
    try {
      setDevicesLoading(true);
      setDevicesError(null);
      const data = await deviceService.getDevices(undefined, undefined, true);
      setDevices(data);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      setDevicesError(t('reports.loadError'));
    } finally {
      setDevicesLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-regina-gray-800 sm:text-2xl">{t('reports.title')}</h1>
        <p className="mt-1 text-sm text-regina-gray-600">{t('reports.subtitle')}</p>
      </div>

      {/* Device Error Message */}
      {devicesError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-regina-error flex-shrink-0" />
          <p className="text-sm text-red-700">{devicesError}</p>
        </div>
      )}

      {/* Devices Section */}
      <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm sm:p-6">

        {/* Devices Grid */}
        {devicesLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <ArrowPathIcon className="w-8 h-8 text-regina-red animate-spin" />
              <p className="text-sm text-regina-gray-600">{t('reports.loading')}</p>
            </div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <ServerIcon className="w-12 h-12 text-regina-gray-400 mx-auto mb-3" />
            <p className="text-regina-gray-600">{t('reports.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {devices.map((device) => (
              <div
                key={device.id}
                onClick={() => navigate(`/device/${device.device_id}`)}
                className="bg-regina-gray-50 border border-regina-gray-200 rounded-lg p-4 hover:border-regina-red hover:shadow-regina-hover transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${device.is_online ? 'bg-regina-success' : 'bg-regina-gray-500'}`} />
                    <h3 className="font-medium text-regina-gray-800">{device.name}</h3>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    device.is_online ? 'bg-green-100 text-green-700' : 'bg-regina-gray-200 text-regina-gray-700'
                  }`}>
                    {device.is_online ? t('common.online') : t('common.offline')}
                  </span>
                </div>
                
                <p className="text-xs text-regina-gray-600 mb-3">{device.device_id}</p>
                
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-regina-gray-600">{t('reports.statusLabel')}</span>
                    <span className="font-medium text-regina-gray-800">{device.current_state || 'idle'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-regina-gray-600">{t('reports.uptimeLabel')}</span>
                    <span className="font-medium text-regina-gray-800">
                      {device.uptime_seconds ? `${Math.floor(device.uptime_seconds / 3600)}h` : t('common.na')}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-regina-gray-600 shrink-0">{t('reports.otaEndpointLabel')}</span>
                    <span className="font-mono text-[11px] font-medium text-regina-gray-800 text-right break-all">
                      {device.ota_public_host
                        ? `${device.ota_public_host}${device.ota_public_port ? `:${device.ota_public_port}` : ''}`
                        : t('common.na')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
